import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingScreen } from "@/components/ProtectedRoute";
import { toast } from "sonner";
import { Calendar, Repeat, CheckCircle2 } from "lucide-react";

export default function Booking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [service, setService] = useState(null);
  const [type, setType] = useState("one_time");
  const [frequency, setFrequency] = useState("weekly");
  const [date, setDate] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [notes, setNotes] = useState("");
  const [addons, setAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get(`/providers/${id}`).then((r) => { setP(r.data); setService(r.data.services?.[0]); }).catch(() => {}); }, [id]);
  if (!p) return <LoadingScreen />;

  const toggleAddon = (a) => setAddons((prev) => prev.find((x) => x.key === a.key) ? prev.filter((x) => x.key !== a.key) : [...prev, a]);
  const total = (service?.price || 0) + addons.reduce((s, a) => s + a.price, 0);

  const submit = async () => {
    if (!user) { toast.error("Please log in to book"); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Only customers can book"); return; }
    if (!date || !address || !zip) { toast.error("Please fill date, address, and ZIP"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        provider_id: id, service_key: service.category, service_name: service.name, base_price: service.price,
        booking_type: type, frequency: type === "recurring" ? frequency : null,
        scheduled_date: date, address, zip_code: zip, notes, addons,
      });
      toast.success("Booking created! Proceeding to payment…");
      const co = await api.post("/payments/checkout", { kind: "booking", booking_id: res.data.booking_id, origin_url: window.location.origin });
      window.location.href = co.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <Layout hideConcierge>
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-3xl font-extrabold text-lawn-ink">Book {p.business_name}</h1>
        <p className="text-muted-foreground mt-1">Complete your booking details below.</p>

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-border rounded-2xl p-6">
              <Label className="font-semibold text-lawn-ink">Select service</Label>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {p.services?.map((s) => (
                  <button key={s.category} data-testid={`book-service-${s.category}`} onClick={() => setService(s)}
                    className={`border-2 rounded-xl p-3 text-left transition-colors ${service?.category === s.category ? "border-lawn bg-lawn-light" : "border-border"}`}>
                    <p className="font-semibold text-sm text-lawn-ink">{s.name}</p>
                    <p className="text-lawn font-bold">${s.price}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-border rounded-2xl p-6">
              <Label className="font-semibold text-lawn-ink">Frequency</Label>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button data-testid="book-onetime" onClick={() => setType("one_time")} className={`border-2 rounded-xl p-3 flex items-center gap-2 ${type === "one_time" ? "border-lawn bg-lawn-light" : "border-border"}`}><Calendar className="w-4 h-4 text-lawn" /> One-time</button>
                <button data-testid="book-recurring" onClick={() => setType("recurring")} className={`border-2 rounded-xl p-3 flex items-center gap-2 ${type === "recurring" ? "border-lawn bg-lawn-light" : "border-border"}`}><Repeat className="w-4 h-4 text-lawn" /> Recurring</button>
              </div>
              {type === "recurring" && (
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger data-testid="book-frequency" className="mt-3 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="bg-white border border-border rounded-2xl p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Preferred date</Label><Input data-testid="book-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 rounded-xl" /></div>
                <div><Label>ZIP code</Label><Input data-testid="book-zip" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="78701" className="mt-1.5 rounded-xl" /></div>
              </div>
              <div><Label>Service address</Label><Input data-testid="book-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" className="mt-1.5 rounded-xl" /></div>
              <div><Label>Notes (optional)</Label><Textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, special requests…" className="mt-1.5 rounded-xl" /></div>
            </div>

            {p.addons?.length > 0 && (
              <div className="bg-white border border-border rounded-2xl p-6">
                <Label className="font-semibold text-lawn-ink">Add-ons</Label>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {p.addons.map((a) => (
                    <label key={a.key} data-testid={`book-addon-${a.key}`} className="border border-border rounded-xl p-3 flex items-center gap-3 cursor-pointer hover:border-lawn">
                      <Checkbox checked={!!addons.find((x) => x.key === a.key)} onCheckedChange={() => toggleAddon(a)} />
                      <div className="flex-1"><p className="text-sm font-medium text-lawn-ink">{a.name}</p></div>
                      <span className="text-sm font-bold text-lawn">+${a.price}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside>
            <div className="bg-white border border-border rounded-2xl p-6 sticky top-24">
              <h3 className="font-display font-bold text-lawn-ink mb-4">Order summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{service?.name}</span><span className="font-semibold">${service?.price}</span></div>
                {addons.map((a) => <div key={a.key} className="flex justify-between"><span className="text-muted-foreground">{a.name}</span><span className="font-semibold">+${a.price}</span></div>)}
                {type === "recurring" && <div className="flex justify-between text-lawn"><span>Frequency</span><span className="capitalize font-semibold">{frequency}</span></div>}
              </div>
              <div className="border-t border-border mt-4 pt-4 flex justify-between items-center">
                <span className="font-semibold text-lawn-ink">Total</span>
                <span className="font-display text-2xl font-extrabold text-lawn">${total.toFixed(2)}</span>
              </div>
              <Button data-testid="book-submit-btn" onClick={submit} disabled={submitting} className="w-full mt-5 rounded-full bg-lawn hover:bg-lawn-dark font-semibold h-11 gap-2">
                <CheckCircle2 className="w-4 h-4" /> {submitting ? "Processing…" : "Confirm & Pay"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center mt-3">Secure payment via Stripe. Test card: 4242 4242 4242 4242</p>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
